import { EntryEditError } from '../edits.ts';
import { formatJavaProperties, parseJavaProperties } from '../javaProperties.ts';
import { javaIsBlank, javaStrip } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection } from '../sql.ts';
import type { CalendarMonth, Festival, Moon, Season } from './calendarEngine.ts';
import { ACTIVE_CALENDAR_SETTING, activeCalendarId } from './calendars.ts';

/**
 * Writing a realm's calendars (docs/PARITY-STEP-3-PLAN.md section 5), as the desktop's
 * `CalendarService` and `CalendarRepository` do: a new calendar has one month of 30 days, a
 * 7-day week and 24 hours; saving writes the whole calendar, its months, seasons, moons and
 * festivals each replaced; deleting the active calendar leaves the realm with none. The
 * numbers are held to the desktop dialogs' limits.
 */

/** Everything about a calendar that's edited, saved together. */
export interface CalendarDetails {
  readonly name: string;
  readonly description: string;
  readonly months: readonly CalendarMonth[];
  readonly daysPerWeek: number;
  readonly weekdayNames: readonly string[];
  readonly hoursPerDay: number;
  readonly epochYear: number;
  readonly seasons: readonly Season[];
  readonly moons: readonly Moon[];
  readonly festivals: readonly Festival[];
}

/** The desktop dialogs' spinners: 1 to 1,000 days, up to 30 days a week and 100 hours a day. */
export const CALENDAR_LIMITS = {
  name: 200,
  description: 4000,
  months: 1000,
  monthLength: 1000,
  daysPerWeek: 30,
  hoursPerDay: 100,
  day: 1000,
  items: 1000,
} as const;

const INT_MIN = -2147483648;
const INT_MAX = 2147483647;

export function createCalendar(conn: SqlConnection, name: string, description: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanDetails({
    name,
    description,
    months: [{ name: 'Month 1', lengthInDays: 30 }],
    daysPerWeek: 7,
    weekdayNames: [],
    hoursPerDay: 24,
    epochYear: 1,
    seasons: [],
    moons: [],
    festivals: [],
  });
  transaction(conn, () => {
    conn.run(
      'INSERT INTO calendars (name, description, days_per_week, weekday_names, hours_per_day, epoch_year, created, modified, id) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)',
      [clean.name, clean.description, clean.daysPerWeek, clean.weekdayNames.join('\n'), clean.hoursPerDay, clean.epochYear, now, now, id],
    );
    writeParts(conn, id, clean);
  });
  return id;
}

export function updateCalendar(conn: SqlConnection, id: string, details: CalendarDetails, now: string = new Date().toISOString()): void {
  const clean = cleanDetails(details);
  transaction(conn, () => {
    requireCalendar(conn, id);
    conn.run(
      'UPDATE calendars SET name = ?, description = ?, days_per_week = ?, weekday_names = ?, hours_per_day = ?, epoch_year = ?, modified = ? WHERE id = ?',
      [clean.name, clean.description, clean.daysPerWeek, clean.weekdayNames.join('\n'), clean.hoursPerDay, clean.epochYear, now, id],
    );
    writeParts(conn, id, clean);
  });
}

export function deleteCalendar(conn: SqlConnection, id: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    requireCalendar(conn, id);
    if (activeCalendarId(conn) === id) writeActive(conn, null, now);
    conn.run('DELETE FROM calendars WHERE id = ?', [id]);
  });
}

/** Makes a calendar the realm's active one, or none with null, in the realm's settings. */
export function setActiveCalendar(conn: SqlConnection, id: string | null, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    if (id !== null) requireCalendar(conn, id);
    writeActive(conn, id, now);
  });
}

/** Weekday names as the desktop's Week dialog reads them: comma-separated, each trimmed. */
export function parseWeekdayNames(typed: string): string[] {
  if (javaIsBlank(typed)) return [];
  const names = typed.split(',');
  // Java's split drops trailing empty pieces.
  while (names.length > 0 && names[names.length - 1] === '') names.pop();
  return names.map(javaStrip);
}

function writeActive(conn: SqlConnection, id: string | null, now: string): void {
  const settings = parseJavaProperties(text(scalar(conn, 'SELECT settings FROM realm_metadata LIMIT 1')));
  if (id === null) settings.delete(ACTIVE_CALENDAR_SETTING);
  else settings.set(ACTIVE_CALENDAR_SETTING, id);
  conn.run('UPDATE realm_metadata SET settings = ?, modified = ?', [formatJavaProperties(settings), now]);
}

function requireCalendar(conn: SqlConnection, id: string): void {
  if (int(scalar(conn, 'SELECT COUNT(*) FROM calendars WHERE id = ?', [id])) === 0) throw new EntryEditError('not_found', 'That calendar no longer exists.');
}

function writeParts(conn: SqlConnection, id: string, clean: CalendarDetails): void {
  for (const table of ['calendar_months', 'calendar_seasons', 'calendar_moons', 'calendar_festivals']) conn.run(`DELETE FROM ${table} WHERE calendar_id = ?`, [id]);
  clean.months.forEach((m, i) => conn.run('INSERT INTO calendar_months (calendar_id, position, name, length_days) VALUES (?, ?, ?, ?)', [id, i, m.name, m.lengthInDays]));
  clean.seasons.forEach((s, i) =>
    conn.run('INSERT INTO calendar_seasons (calendar_id, position, name, start_month, start_day, end_month, end_day) VALUES (?, ?, ?, ?, ?, ?, ?)', [
      id,
      i,
      s.name,
      s.startMonth,
      s.startDay,
      s.endMonth,
      s.endDay,
    ]),
  );
  clean.moons.forEach((m, i) =>
    conn.run('INSERT INTO calendar_moons (calendar_id, position, name, cycle_length_days, phase_offset_days) VALUES (?, ?, ?, ?, ?)', [id, i, m.name, m.cycleLengthDays, m.phaseOffsetDays]),
  );
  clean.festivals.forEach((f, i) =>
    conn.run('INSERT INTO calendar_festivals (calendar_id, position, name, description, month_index, day, year) VALUES (?, ?, ?, ?, ?, ?, ?)', [
      id,
      i,
      f.name,
      f.description,
      f.monthIndex,
      f.day,
      f.year,
    ]),
  );
}

const invalid = (message: string) => new EntryEditError('invalid', message);

function wholeNumber(value: number, min: number, max: number, what: string): number {
  if (!Number.isInteger(value) || value < min || value > max) throw invalid(`${what} must be a whole number from ${min.toLocaleString('en')} to ${max.toLocaleString('en')}.`);
  return value;
}

/** As the desktop's constructors: a blank name becomes the default, otherwise it's kept as written. */
function nameOf(value: string, fallback: string, what: string): string {
  if (value.length > CALENDAR_LIMITS.name) throw invalid(`${what} can be at most ${CALENDAR_LIMITS.name} characters.`);
  const flat = value.replace(/[\r\n]+/g, ' ');
  return javaIsBlank(flat) ? fallback : flat;
}

function cleanDetails(d: CalendarDetails): CalendarDetails {
  if (d.description.length > CALENDAR_LIMITS.description) throw invalid(`A description can be at most ${CALENDAR_LIMITS.description.toLocaleString('en')} characters.`);
  if (d.months.length === 0) throw invalid('A calendar needs at least one month.');
  if (d.months.length > CALENDAR_LIMITS.months) throw invalid(`A calendar can have at most ${CALENDAR_LIMITS.months} months.`);
  for (const list of [d.seasons, d.moons, d.festivals, d.weekdayNames]) {
    if (list.length > CALENDAR_LIMITS.items) throw invalid(`A calendar can have at most ${CALENDAR_LIMITS.items} of each.`);
  }
  // A season or festival may name a month past the year's end, as the desktop leaves one when
  // a month is removed; the engine reads it as in no month, so it's kept rather than refused.
  const month = (index: number, what: string) => wholeNumber(index, 0, CALENDAR_LIMITS.months - 1, what);
  const day = (value: number, what: string) => wholeNumber(value, 1, CALENDAR_LIMITS.day, what);
  return {
    name: nameOf(d.name, 'Untitled Calendar', "A calendar's name"),
    description: d.description,
    months: d.months.map((m, i) => ({
      name: nameOf(m.name, 'Month', `Month ${i + 1}'s name`),
      lengthInDays: wholeNumber(m.lengthInDays, 1, CALENDAR_LIMITS.monthLength, `Month ${i + 1}'s length`),
    })),
    daysPerWeek: wholeNumber(d.daysPerWeek, 1, CALENDAR_LIMITS.daysPerWeek, 'Days per week'),
    // A weekday name may be left empty, as the desktop keeps "Sunday, , Tyrsday".
    weekdayNames: d.weekdayNames.map((n) => nameOf(n, '', 'A weekday name')),
    hoursPerDay: wholeNumber(d.hoursPerDay, 1, CALENDAR_LIMITS.hoursPerDay, 'Hours per day'),
    epochYear: wholeNumber(d.epochYear, INT_MIN, INT_MAX, 'The epoch year'),
    seasons: d.seasons.map((s, i) => ({
      name: nameOf(s.name, 'Season', `Season ${i + 1}'s name`),
      startMonth: month(s.startMonth, `Season ${i + 1}'s start month`),
      startDay: day(s.startDay, `Season ${i + 1}'s start day`),
      endMonth: month(s.endMonth, `Season ${i + 1}'s end month`),
      endDay: day(s.endDay, `Season ${i + 1}'s end day`),
    })),
    moons: d.moons.map((m, i) => {
      if (!Number.isFinite(m.cycleLengthDays) || m.cycleLengthDays <= 0 || m.cycleLengthDays > 1e9) throw invalid(`Moon ${i + 1}'s cycle must be more than 0 days.`);
      if (!Number.isFinite(m.phaseOffsetDays) || Math.abs(m.phaseOffsetDays) > 1e15) throw invalid(`Moon ${i + 1}'s phase offset must be a number of days.`);
      return { name: nameOf(m.name, 'Moon', `Moon ${i + 1}'s name`), cycleLengthDays: m.cycleLengthDays, phaseOffsetDays: m.phaseOffsetDays };
    }),
    festivals: d.festivals.map((f, i) => {
      if (f.description.length > CALENDAR_LIMITS.description) throw invalid(`Festival ${i + 1}'s description is too long.`);
      return {
        name: nameOf(f.name, 'Festival', `Festival ${i + 1}'s name`),
        description: f.description,
        monthIndex: month(f.monthIndex, `Festival ${i + 1}'s month`),
        day: day(f.day, `Festival ${i + 1}'s day`),
        year: f.year === null ? null : wholeNumber(f.year, INT_MIN, INT_MAX, `Festival ${i + 1}'s year`),
      };
    }),
  };
}
